/* flex-cart-sync.js
   Goal: Keep "Mobile Flex" in sync with phones:
   - If ANY phone exists in cart -> ensure Flex exists (qty 1)
   - If NO phones exist -> remove Flex
   Works with Dawn + widgets because it relies on /cart.js + keys, not DOM indices.
*/
(() => {
  if (window.__FLEX_CART_SYNC_INSTALLED__) return;
  window.__FLEX_CART_SYNC_INSTALLED__ = true;

  /********************
   * CONFIG (SET THESE)
   ********************/
  const FLEX_VARIANT_ID = 44270260486237; // <-- TODO: PUT YOUR MOBILE FLEX VARIANT ID HERE

  function isPhoneItem(item) {
    // Always exclude the Flex plan itself first
    if (item?.variant_id === FLEX_VARIANT_ID) return false;

    // ✅ PRIMARY: Shopify product_type set to "Phones" in admin
    if ((item?.product_type || "").toLowerCase() === "phones") return true;

    // ✅ SECONDARY: tags include "Phones" (cart.js returns tags as a comma-separated string)
    const tagsRaw = String(item?.tags || "");
    const tagList = tagsRaw.split(",").map((t) => t.trim().toLowerCase());
    if (tagList.includes("phones")) return true;

    // FALLBACK: original vendor/title/type heuristics
    const title = (item?.product_title || "").toLowerCase();
    const vendor = (item?.vendor || "").toLowerCase();
    const type = (item?.product_type || "").toLowerCase();

    const brandMatch = ["apple", "samsung", "motorola", "google", "blu", "nokia", "tcl", "oneplus"]
      .some((b) => vendor.includes(b));
    const titleMatch = /(iphone|galaxy|pixel|moto|motorola|blu)\b/.test(title);
    const typeMatch = type.includes("phone");

    const isFlex =
      title.includes("mobile flex") || title.includes("flex");

    return !isFlex && (brandMatch || titleMatch || typeMatch);
  }

  /********************
   * INTERNALS
   ********************/
  const CART_ENDPOINT_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  let running = false;
  let queued = false;

  async function getCart() {
    const r = await fetch("/cart.js", { credentials: "same-origin" });
    return r.json();
  }

  async function addFlex() {
    await fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: FLEX_VARIANT_ID, quantity: 1 }] }),
    });
  }

  async function removeFlexByKey(key) {
    await fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
  }

  window.FLEX_SYNC = window.FLEX_SYNC || (async function FLEX_SYNC() {
    const cart = await getCart();

    const hasPhone = cart.items.some(isPhoneItem);
    const flexLine = cart.items.find((i) => i.variant_id === FLEX_VARIANT_ID);

    if (hasPhone && !flexLine) {
      await addFlex();
      return;
    }

    if (!hasPhone && flexLine) {
      await removeFlexByKey(flexLine.key);
      return;
    }
  });

  async function runSync() {
    if (running) {
      queued = true;
      return;
    }
    running = true;

    try {
      await sleep(50);
      await window.FLEX_SYNC();
    } catch (e) {
      console.warn("[FLEX] sync failed:", e);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        runSync();
      }
    }
  }

  const scheduleSync = debounce(runSync, 200);

  /********************
   * TRIGGERS (Widget-proof)
   ********************/

  // 1) Patch fetch
  const _fetch = window.fetch;
  window.fetch = async (...args) => {
    const url = typeof args[0] === "string" ? args[0] : (args[0] && args[0].url) || "";
    const isCartCall = CART_ENDPOINT_RE.test(String(url));

    const res = await _fetch(...args);

    if (isCartCall && res && res.ok) {
      if (!running) scheduleSync();
    }

    return res;
  };

  // 2) Patch XHR
  const _open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__isFlexCartCall = CART_ENDPOINT_RE.test(String(url || ""));
    return _open.call(this, method, url, ...rest);
  };

  const _send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (...args) {
    this.addEventListener("load", () => {
      if (this.__isFlexCartCall && this.status >= 200 && this.status < 300) {
        if (!running) scheduleSync();
      }
    });
    return _send.apply(this, args);
  };

  // 3) Minimal DOM observer
  function attachObserver() {
    const targets = [
      document.querySelector("cart-items"),
      document.querySelector("cart-drawer"),
      document.getElementById("main-cart-items"),
      document.getElementById("CartDrawer"),
    ].filter(Boolean);

    if (!targets.length) return;

    const obs = new MutationObserver((mutations) => {
      if (running) return;
      const meaningful = mutations.some(
        (m) => (m.addedNodes && m.addedNodes.length) || (m.removedNodes && m.removedNodes.length)
      );
      if (meaningful) scheduleSync();
    });

    targets.forEach((t) => obs.observe(t, { childList: true, subtree: true }));
  }

  window.addEventListener("load", () => {
    attachObserver();
    scheduleSync();
  });

  console.log("[FLEX] flex-cart-sync installed");
})();